/**
 * A single picker row inside `ChangeLanguageModal`.
 *
 * Used for both the Original (source) row and each translation row.
 * Clicking it fires `onSelect`. The row shows:
 *  - A trailing "You're viewing" pill when it matches the user's
 *    current primary doc, so the user knows which row collapses the
 *    split pane when clicked.
 *  - A trailing checkmark when it matches the active target
 *    (whichever doc the dialog is currently targeting).
 *  - A "Click to close split view" hint below the button when this
 *    row is the primary AND split view is active.
 */

import { Check } from 'lucide-react';

export function SelectableRow({
  label,
  isPrimary,
  isActive,
  isPrimaryAndSplit,
  idleBg,
  onSelect,
  viewingLabel,
  collapseHint,
  activeColor,
  idleColor,
  borderColor,
  textColor
}: {
  label: string;
  isPrimary: boolean;
  isActive: boolean;
  isPrimaryAndSplit: boolean;
  idleBg: string;
  onSelect: () => void;
  viewingLabel: string;
  collapseHint: string;
  activeColor: string;
  idleColor: string;
  borderColor: string;
  textColor: string;
}) {
  return (
    <>
      <button
        type="button"
        role="radio"
        aria-checked={isActive}
        onClick={onSelect}
        title={isPrimaryAndSplit ? collapseHint : undefined}
        className="flex items-center justify-between rounded-xl border px-3 py-2 text-sm text-left cursor-pointer hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        style={{ borderColor, backgroundColor: idleBg, color: textColor }}
      >
        <span className="truncate">{label}</span>
        {(isPrimary || isActive) && (
          <span className="inline-flex items-center gap-1.5 shrink-0">
            {isPrimary && (
              <span
                className="text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-full border"
                style={{
                  borderColor: activeColor,
                  color: activeColor,
                  backgroundColor: 'transparent'
                }}
              >
                {viewingLabel}
              </span>
            )}
            {isActive && (
              <Check size={14} aria-hidden style={{ color: idleColor }} />
            )}
          </span>
        )}
      </button>
      {isPrimaryAndSplit && (
        <p className="text-xs opacity-70 mt-0.5" style={{ color: idleColor }}>
          {collapseHint}
        </p>
      )}
    </>
  );
}
